import { Drawer } from "@mui/material";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useWasteLineUnifiedSuspense } from "hooks/useWasteLineUnified";
import { Suspense, useState } from "react";

import { LineItemDetailDialog } from "../ManifestLineItems/LineItemDetailDialog";
import { LineItemsDataGrid } from "./LineItemsDataGrid";
import { ManifestDetailSection, ManifestDetailSectionSkeleton } from "./ManifestDetailSection";
import { ManifestDetails as ManifestDetailsV1 } from "./ManifestDetailsV1";

interface ManifestDetailDrawerProps {
  /**
   * Identifies the waste line the drawer was opened for. The drawer fetches
   * the line to look up its parent manifest, then renders the full manifest
   * detail with the embedded line-items grid pre-filtered to this line.
   */
  lineItemId: string | undefined;
  open: boolean;
  onClose: () => void;
}

/**
 * Drawer opened when a deep link points at a specific waste line — e.g. from
 * the tenant Waste Streams modal or the all-line-items page. Hosts the full
 * manifest detail (so the user lands on the line's parent manifest, line
 * items table and all) with the embedded grid pre-filtered to the targeted
 * line. Clearing the filter reveals the rest of the manifest. Clicking a row
 * inside the grid opens `LineItemDetailDialog` for view/edit, mirroring the
 * manifests-page drawer's UX.
 */
export function ManifestDetailDrawer({ lineItemId, open, onClose }: ManifestDetailDrawerProps) {
  const showManifestViewsV2 = useManifestViewsV2();

  // The reworked manifest detail is part of the manifest-views-v2 surface, but
  // this drawer mounts on pages that aren't v2-gated (the Facility page and the
  // all-line-items page). Mirror ManifestsView: v2 off → the pre-rework drawer
  // (ManifestDetailsV1 renders its own <Drawer> hosting the V1 section/grid).
  if (!showManifestViewsV2) {
    if (!open) return null;
    return (
      <Suspense fallback={null}>
        <V1DrawerForLineItem lineItemId={lineItemId} onClose={onClose} />
      </Suspense>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          // Wider than the original line-item panel — the embedded line-items
          // grid needs the room to fit Amount, Management Method, etc. without
          // truncation — but capped so the underlying waste-stream modal (1350)
          // still shows on its left edge.
          width: { xs: "100%", sm: 700, md: 900, lg: 1100, xl: 1250 },
          boxSizing: "border-box",
        },
      }}
    >
      {open && (
        <Suspense fallback={<ManifestDetailSectionSkeleton />}>
          <ManifestDetailDrawerContent lineItemId={lineItemId} onClose={onClose} />
        </Suspense>
      )}
    </Drawer>
  );
}

/**
 * v2-off fallback: resolve the targeted waste line to its parent manifest, then
 * hand off to the pre-rework V1 drawer (which manages its own line selection).
 */
const V1DrawerForLineItem = ({
  lineItemId,
  onClose,
}: {
  lineItemId: string | undefined;
  onClose: () => void;
}) => {
  const { lineItem } = useWasteLineUnifiedSuspense(lineItemId);
  return (
    <ManifestDetailsV1 processedManifestId={lineItem?.processedManifestId} open onClose={onClose} />
  );
};

const ManifestDetailDrawerContent = ({
  lineItemId,
  onClose,
}: {
  lineItemId: string | undefined;
  onClose: () => void;
}) => {
  const { lineItem } = useWasteLineUnifiedSuspense(lineItemId);
  const processedManifestId = lineItem?.processedManifestId;
  // Local state so the dialog can be opened/closed independently of the
  // drawer. The parent's URL param drives which line opened the drawer; this
  // state drives which line (if any) is currently in the detail dialog on top.
  const [editingLineItemId, setEditingLineItemId] = useState<string | undefined>(undefined);
  return (
    <>
      <ManifestDetailSection processedManifestId={processedManifestId} onClose={onClose}>
        <LineItemsDataGrid
          processedManifestId={processedManifestId}
          initialWasteLineNumber={lineItem?.wasteLineNumber ?? undefined}
          onRowClick={setEditingLineItemId}
        />
      </ManifestDetailSection>
      {editingLineItemId && (
        <Suspense fallback={null}>
          <LineItemDetailDialog
            key={editingLineItemId}
            processedManifestId={processedManifestId}
            lineItemId={editingLineItemId}
            onClose={() => setEditingLineItemId(undefined)}
          />
        </Suspense>
      )}
    </>
  );
};
